'use client';

import React from 'react';
import Link from 'next/link';

export default function LandingPage() {
  // Left side — two tiles side by side
  const killScenariosPanel = {
    id: 'kill-scenarios',
    label: 'Kill Scenarios',
    tag: 'Video Scenario',
    description: 'Test how fast you can react',
    href: '/operator-react',
    accent: '#ff4655',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" />
        <rect x="3" y="6" width="12" height="12" rx="2" />
      </svg>
    ),
    bgGradient: 'linear-gradient(160deg, #1a0a0c 0%, #0d0d0d 60%, #110608 100%)',
    borderColor: 'rgba(255,70,85,0.18)',
    hoverBorder: 'rgba(255,70,85,0.45)',
    cta: 'Enter',
  };

  const esportScenariosPanel = {
    id: 'esport-scenarios',
    label: 'Esport Scenarios',
    tag: 'Multi-Reaction',
    description: 'Multiple peeks per clip. React to every one or fail.',
    href: '/scenarios',
    accent: '#00e5ff',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="1" fill="currentColor" />
        <line x1="12" y1="3" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="21" />
        <line x1="3" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="21" y2="12" />
      </svg>
    ),
    bgGradient: 'linear-gradient(160deg, #001a1f 0%, #0d0d0d 55%, #001215 100%)',
    borderColor: 'rgba(0,229,255,0.18)',
    hoverBorder: 'rgba(0,229,255,0.5)',
    cta: 'Enter',
  };

  // Right side — stacked
  const customGamePanel = {
    id: 'custom-game',
    label: 'Angle Holder',
    tag: 'Aim Trainer',
    description: 'Maps, angles, Simulation Mode, crosshair system — the full training suite.',
    href: '/simulator',
    accent: '#ffd700',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    bgGradient: 'linear-gradient(160deg, #1a1500 0%, #0d0d0d 55%, #111000 100%)',
    borderColor: 'rgba(255,215,0,0.15)',
    hoverBorder: 'rgba(255,215,0,0.4)',
    cta: 'Enter Angle Holder',
  };

  const customScenarioPanel = {
    id: 'custom-scenario',
    label: 'Custom Scenario',
    tag: 'Local Builder',
    description: 'Load your own video, tag reaction moments, and play your custom scenario.',
    href: '/custom-scenario',
    accent: '#ff8c42',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" />
        <rect x="3" y="6" width="12" height="12" rx="2" />
        <line x1="9" y1="10" x2="9" y2="14" />
        <line x1="7" y1="12" x2="11" y2="12" />
      </svg>
    ),
    bgGradient: 'linear-gradient(160deg, #1a0d00 0%, #0d0d0d 55%, #120a00 100%)',
    borderColor: 'rgba(255,140,66,0.15)',
    hoverBorder: 'rgba(255,140,66,0.4)',
    cta: 'Build Scenario',
  };

  function LeftTile({ panel }: { panel: typeof killScenariosPanel }) {
    return (
      <Link href={panel.href} className="flex flex-1 flex-col no-underline" style={{ minWidth: 0 }}>
        <div
          className="group relative flex flex-1 cursor-pointer flex-col overflow-hidden rounded-2xl transition-all duration-300"
          style={{
            background: panel.bgGradient,
            border: `1px solid ${panel.borderColor}`,
            minHeight: 300,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = panel.hoverBorder;
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = panel.borderColor;
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
          }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: `radial-gradient(ellipse at 50% 0%, ${panel.accent}12 0%, transparent 65%)` }} />
          <div className="absolute right-4 top-4 z-10 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: panel.accent, boxShadow: `0 0 6px ${panel.accent}` }} />
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: panel.accent }}>Live</span>
          </div>
          <div className="flex flex-1 flex-col justify-between p-6">
            <div>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105"
                style={{ background: `${panel.accent}14`, color: panel.accent, border: `1px solid ${panel.accent}22` }}>
                {panel.icon}
              </div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest" style={{ color: `${panel.accent}99` }}>{panel.tag}</p>
              <h2 className="mb-2 text-xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>{panel.label}</h2>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>{panel.description}</p>
            </div>
            <div className="mt-6">
              <div className="flex items-center gap-2 text-sm font-semibold transition-all duration-200 group-hover:gap-3" style={{ color: panel.accent }}>
                <span>{panel.cta}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: `linear-gradient(90deg, transparent, ${panel.accent}60, transparent)` }} />
        </div>
      </Link>
    );
  }

  function RightSmallTile({ panel }: { panel: typeof customGamePanel }) {
    return (
      <Link href={panel.href} className="no-underline flex flex-col">
        <div
          className="group relative flex cursor-pointer flex-row items-center overflow-hidden rounded-xl transition-all duration-300"
          style={{
            background: 'rgba(255,255,255,0.025)',
            border: `1px solid rgba(255,255,255,0.07)`,
            minHeight: 36,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = panel.hoverBorder;
            (e.currentTarget as HTMLDivElement).style.background = panel.bgGradient;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.07)';
            (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.025)';
          }}
        >
          <div className="flex flex-1 flex-row items-center gap-3 px-4 py-2">
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ background: `${panel.accent}14`, color: `${panel.accent}99` }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" />
                <rect x="3" y="6" width="12" height="12" rx="2" />
                <line x1="9" y1="10" x2="9" y2="14" />
                <line x1="7" y1="12" x2="11" y2="12" />
              </svg>
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>{panel.tag}</span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{panel.label}</span>
          </div>
          <div className="flex-shrink-0 pr-4">
            <div className="flex items-center gap-1 text-xs transition-all duration-200 group-hover:gap-2" style={{ color: `${panel.accent}70` }}>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{panel.cta}</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: `${panel.accent}70` }}>
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div
      className="relative flex min-h-screen flex-col"
      style={{ background: '#0a0a0a', fontFamily: 'var(--font-geist), sans-serif' }}
    >
      {/* Noise texture */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.025]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6">
        <div className="flex items-center gap-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ff4655" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="1" fill="#ff4655" />
            <line x1="12" y1="3" x2="12" y2="6" />
            <line x1="12" y1="18" x2="12" y2="21" />
            <line x1="3" y1="12" x2="6" y2="12" />
            <line x1="18" y1="12" x2="21" y2="12" />
          </svg>
          <span className="text-base font-bold tracking-tight text-white" style={{ letterSpacing: '-0.01em' }}>
            fps<span style={{ color: '#ff4655' }}>.</span>expert
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://9k.gg"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-mono transition-colors"
            style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.75)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.35)'; }}
          >
            <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>9k.gg</span>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>— Check out →</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="relative z-10 px-8 pb-8 pt-4 text-center">
        <h1 className="mb-3 text-4xl font-bold tracking-tight text-white md:text-5xl" style={{ letterSpacing: '-0.03em' }}>
          Choose your training mode
        </h1>
        <p className="mx-auto max-w-md text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Testing your reaction speed whilst you wait in queue.
        </p>
      </div>

      {/* Panels — Three columns top row | Local Builder full-width bottom */}
      <main className="relative z-10 flex flex-1 flex-col gap-4 px-4 pb-10 md:px-8">

        {/* TOP ROW — Kill Scenarios | Angle Holder | Esport Scenarios */}
        <div className="flex flex-col gap-4 md:flex-row md:gap-4" style={{ flex: '1 1 0', minWidth: 0 }}>
          <LeftTile panel={killScenariosPanel} />
          <LeftTile panel={customGamePanel} />
          <LeftTile panel={esportScenariosPanel} />
        </div>

        {/* BOTTOM ROW — Custom Scenario (Local Builder) full width */}
        <RightSmallTile panel={customScenarioPanel} />
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t px-8 py-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="relative flex items-center justify-between">
          <a
            href="https://x.com/tiny9k"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs transition-colors"
            style={{ color: 'rgba(255,255,255,0.2)', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.5)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.2)'; }}
          >
            Created by <span style={{ color: 'rgba(255,255,255,0.35)' }}>@tiny9k</span>
          </a>
          <p className="absolute left-1/2 -translate-x-1/2 text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
            fps<span style={{ color: '#ff4655' }}>.</span>expert — browser-based reaction time training
          </p>
          <Link
            href="/timestamp-tool"
            className="text-xs font-mono uppercase tracking-widest transition-colors"
            style={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.7)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.3)'; }}
          >
            Timestamp Tool
          </Link>
        </div>
      </footer>
    </div>
  );
}